import { createSentryPiniaPlugin } from '@sentry/vue';
import { createApp, markRaw } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';

// You can use the following starter router instead of the default one as a clean starting point
// import router from "./router/starter";
import router from '@/router';

// Main Stylesheet
import '@/../scss/main.scss';

// All color themes are included and available by default
// Feel free to comment out any of them if you won't use them in your project
import '@/../scss/oneui/themes/amethyst.scss';
import '@/../scss/oneui/themes/city.scss';
import '@/../scss/oneui/themes/flat.scss';
import '@/../scss/oneui/themes/modern.scss';
import '@/../scss/oneui/themes/smooth.scss';

// Template components
import BaseBlock from '@/components/BaseBlock.vue';
import BaseBackground from '@/components/BaseBackground.vue';
import BasePageHeading from '@/components/BasePageHeading.vue';

// Template directives
import clickRipple from '@/directives/clickRipple';

import { configureEcho } from '@laravel/echo-vue';

configureEcho({
    broadcaster: 'reverb',
});

// Craft new application
const app = createApp(App);

// Register global components
app.component('BaseBlock', BaseBlock);
app.component('BaseBackground', BaseBackground);
app.component('BasePageHeading', BasePageHeading);

// Register global directives
app.directive('click-ripple', clickRipple);

import Swal from 'sweetalert2';
window.Swal = Swal;

// Use Pinia and Vue Router
const pinia = createPinia();
pinia.use(createSentryPiniaPlugin());
pinia.use(({ store }) => {
    store.router = markRaw(router);
});
app.use(pinia);
app.use(router);

// ..and finally mount it!
app.mount('#app');

import 'bootstrap-vue-next/dist/bootstrap-vue-next.css';

/**
 * We'll load the axios HTTP library which allows us to easily issue requests
 * to our Laravel back-end. This library automatically handles sending the
 * CSRF token as a header based on the value of the "XSRF" token cookie.
 */
import type { AxiosError, InternalAxiosRequestConfig } from 'axios';
import axios from 'axios';

import { useAuthStore } from '@/stores/authStore';

axios.defaults.headers.common['X-Requested-With'] = 'Sellopod Web App';
axios.defaults.withCredentials = true;

// Handle 401 responses by logging out and redirecting to login
axios.interceptors.response.use(
    (response) => response,
    async (error: unknown) => {
        if (!axios.isAxiosError(error)) {
            return Promise.reject(error);
        }

        const axiosError = error as AxiosError<LaravelErrorJson>;
        const originalRequest = axiosError.config as InternalAxiosRequestConfig & { _retry?: boolean };

        // Handle 401 Unauthorized responses
        // Skip if: already retried, no config, or request was to /api/users/me (prevents infinite loop during auth check)
        if (
            axiosError.response?.status === 401 &&
            originalRequest &&
            !originalRequest._retry &&
            !originalRequest.url?.includes('api/users/me')
        ) {
            originalRequest._retry = true;

            const authStore = useAuthStore();
            await authStore.logout();
            router.push({ name: 'login' });
        }

        // Handle 419 CSRF token mismatch - session has expired, redirect to login
        if (axiosError.response?.status === 419) {
            const authStore = useAuthStore();
            authStore.setLoggedOut();
            router.push({ name: 'login' });
        }

        // Convert to LaravelError for consistent error handling
        return Promise.reject(LaravelError.fromAxios(axiosError));
    },
);

import {
    ArcElement,
    BarElement,
    CategoryScale,
    Chart as ChartJS,
    Filler,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    TimeScale,
    Title,
    Tooltip,
} from 'chart.js';
import 'chartjs-adapter-luxon';
import annotationPlugin from 'chartjs-plugin-annotation';

import type { LaravelErrorJson } from '@/api/model/LaravelError';
import LaravelError from '@/api/model/LaravelError';
ChartJS.register(
    Title,
    Tooltip,
    Legend,
    BarElement,
    CategoryScale,
    LinearScale,
    ArcElement,
    LineElement,
    PointElement,
    TimeScale,
    Filler,
    annotationPlugin,
);

import * as Sentry from '@sentry/vue';

if (import.meta.env['VITE_SENTRY_DSN_PUBLIC']) {
    const sensitiveKeys =
        /password|secret|token|authorization|cookie|csrf|xsrf|api_key|private_key|session|bearer|credential/i;

    function stripSensitiveData(obj: Record<string, unknown>): Record<string, unknown> {
        const cleaned: Record<string, unknown> = {};
        for (const [key, value] of Object.entries(obj)) {
            if (sensitiveKeys.test(key)) {
                cleaned[key] = '[Filtered]';
            } else if (value && typeof value === 'object' && !Array.isArray(value)) {
                cleaned[key] = stripSensitiveData(value as Record<string, unknown>);
            } else {
                cleaned[key] = value;
            }
        }
        return cleaned;
    }

    Sentry.init({
        app,
        dsn: import.meta.env['VITE_SENTRY_DSN_PUBLIC'] as string,
        beforeSend(event) {
            if (event.request?.data && typeof event.request.data === 'object') {
                event.request.data = stripSensitiveData(event.request.data as Record<string, unknown>);
            }
            if (event.request?.headers) {
                event.request.headers = stripSensitiveData(event.request.headers as Record<string, unknown>) as Record<
                    string,
                    string
                >;
            }

            // Check if it is an exception, and if so, show the report dialog
            if (event.exception) {
                Sentry.showReportDialog({ eventId: event.event_id });
            }
            return event;
        },
        beforeBreadcrumb(breadcrumb) {
            if (breadcrumb.category === 'xhr' || breadcrumb.category === 'fetch') {
                if (breadcrumb.data && typeof breadcrumb.data === 'object') {
                    for (const key of Object.keys(breadcrumb.data)) {
                        if (sensitiveKeys.test(key)) {
                            breadcrumb.data[key] = '[Filtered]';
                        }
                    }
                }
            }
            return breadcrumb;
        },
        integrations: [
            Sentry.browserTracingIntegration({
                router: router,
            }),
            Sentry.vueIntegration({
                tracingOptions: {
                    trackComponents: true,
                },
            }),
        ],
        // Set tracesSampleRate to 1.0 to capture 100%
        // of transactions for performance monitoring.
        // We recommend adjusting this value in production
        tracesSampleRate: (import.meta.env['VITE_SENTRY_TRACES_SAMPLE_RATE'] as number) ?? 0.2,
    });
}

import.meta.glob(['../assets/**/*.png', '../assets/**/*.ico']);
